import { useState, useRef } from 'react'
import apiClient from '../../api/apiClient'
import { AxiosError } from 'axios'

interface FeedbackImage {
  id: number
  originalFilename: string
  imageUrl: string
  fileSize: number
}

interface ImageUploadProps {
  feedbackId: number | null
  images: FeedbackImage[]
  onImagesChange: (images: FeedbackImage[]) => void
  disabled?: boolean
  maxImages?: number
  maxFileSize?: number
}

export function ImageUpload({
  feedbackId,
  images,
  onImagesChange,
  disabled = false,
  maxImages = 5,
  maxFileSize = 7 * 1024 * 1024, // 7MB
}: ImageUploadProps) {
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    if (images.length >= maxImages) {
      setError(`이미지는 최대 ${maxImages}장까지 업로드할 수 있습니다.`)
      return
    }

    const file = files[0]

    if (file.size > maxFileSize) {
      setError('파일 크기는 7MB 이하여야 합니다.')
      return
    }

    if (!file.type.startsWith('image/')) {
      setError('이미지 파일만 업로드할 수 있습니다.')
      return
    }

    setError(null)

    if (feedbackId) {
      // 피드백이 이미 생성된 경우 서버에 업로드
      await uploadToServer(file)
    } else {
      // 피드백 생성 전이면 로컬 프리뷰만 추가
      addLocalPreview(file)
    }

    // 파일 입력 초기화
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const uploadToServer = async (file: File) => {
    if (!feedbackId) return

    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await apiClient.post(`/api/feedbacks/${feedbackId}/images`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      onImagesChange([...images, response.data])
    } catch (err) {
      if (err instanceof AxiosError) {
        setError(err.response?.data?.message || '이미지 업로드에 실패했습니다.')
      } else {
        setError('이미지 업로드에 실패했습니다.')
      }
    } finally {
      setUploading(false)
    }
  }

  const addLocalPreview = (file: File) => {
    const reader = new FileReader()
    reader.onload = () => {
      const preview: FeedbackImage = {
        id: -Date.now(), // 임시 ID (음수로 구분)
        originalFilename: file.name,
        imageUrl: reader.result as string,
        fileSize: file.size,
      }
      onImagesChange([...images, preview])
    }
    reader.readAsDataURL(file)
  }

  const handleRemove = async (image: FeedbackImage) => {
    if (image.id > 0 && feedbackId) {
      // 서버에서 삭제
      try {
        await apiClient.delete(`/api/feedbacks/${feedbackId}/images/${image.id}`)
      } catch (err) {
        if (err instanceof AxiosError) {
          setError(err.response?.data?.message || '이미지 삭제에 실패했습니다.')
        }
        return
      }
    }
    onImagesChange(images.filter((img) => img.id !== image.id))
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          disabled={disabled || uploading || images.length >= maxImages}
          className="hidden"
          id="image-upload"
        />
        <label
          htmlFor="image-upload"
          className={`
            inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed
            transition-all duration-200 cursor-pointer
            ${disabled || uploading || images.length >= maxImages
              ? 'border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50 dark:border-gray-700 dark:bg-gray-800'
              : 'border-gray-300 text-gray-600 hover:border-blue-400 hover:text-blue-600 dark:border-gray-600 dark:text-gray-400 dark:hover:border-blue-500 dark:hover:text-blue-400'
            }
          `}
        >
          {uploading ? (
            <>
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              업로드 중...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              이미지 첨부
            </>
          )}
        </label>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {images.length}/{maxImages} (최대 7MB/개)
        </span>
      </div>

      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
          {images.map((image) => (
            <div key={image.id} className="relative group">
              <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800">
                <img
                  src={image.imageUrl}
                  alt={image.originalFilename}
                  className="w-full h-full object-cover"
                />
              </div>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemove(image)}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full
                           opacity-0 group-hover:opacity-100 transition-opacity duration-200
                           flex items-center justify-center hover:bg-red-600"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 truncate">
                {formatFileSize(image.fileSize)}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
